import * as React from 'react';

import { Selector as AddressSelector } from '@admin/components/address/selector';
import { Address } from '@admin/types/address';

interface IRegion {
  id: string;
  tz: string;
}

interface IOrder {
  address?: Address;
  region?: IRegion;
}

interface IAccount {
  id: string;
}

interface IGeocodeResult {
  potentially_invalid: boolean;
  region?: IRegion;
}

interface IOrderAddressSelectorProps {
  account: IAccount;
  order: IOrder;
  showAccountAddresses?: boolean;
  warehouseReturn?: boolean;
  onAddressSelect?: (address: Address) => void;
  onRegionChange?: (region?: IRegion) => void;
}

/**
 * Component for selecting an address for a non-moving order (pickup, return, etc.).
 *
 * @param props.account - The account object containing account information
 * @param props.order - The order object containing address and region
 * @param props.showAccountAddresses - Whether to show account addresses in the selector
 * @param props.warehouseReturn - Whether this is a warehouse return order
 * @param props.onAddressSelect - Callback when an address is selected
 * @param props.onRegionChange - Callback when the region changes
 */
const OrderAddressSelector: React.FC<IOrderAddressSelectorProps> = ({
  account,
  order,
  showAccountAddresses,
  warehouseReturn,
  onAddressSelect,
  onRegionChange,
}) => {
  const [potentiallyInvalid, setPotentiallyInvalid] = React.useState(false);
  const [selectedAddress, setSelectedAddress] = React.useState<Address | undefined>(order.address);

  const onSelect = React.useCallback(
    (address: Address) => {
      order.address = address;
      setSelectedAddress(address);
      if (onAddressSelect) {
        onAddressSelect(address);
      }
    },
    [order, onAddressSelect],
  );

  const onGeocode = React.useCallback(
    (geocode: IGeocodeResult) => {
      setPotentiallyInvalid(geocode.potentially_invalid);
      // Only update region if not a warehouse return with existing region
      if (!(warehouseReturn && order.region)) {
        order.region = geocode.region;
        if (onRegionChange) {
          onRegionChange(geocode.region);
        }
      }
    },
    [order, warehouseReturn, onRegionChange],
  );

  const onWarehouseSelect = React.useCallback(
    (region: IRegion) => {
      order.region = region;
      if (onRegionChange) {
        onRegionChange(region);
      }
    },
    [order, onRegionChange],
  );

  return (
    <div data-qa="order-address-selector">
      <AddressSelector
        selection={selectedAddress}
        account={account}
        showAccountAddresses={showAccountAddresses}
        onSelect={onSelect}
        potentiallyInvalid={potentiallyInvalid}
        warehouseReturn={warehouseReturn}
        onWarehouseSelect={onWarehouseSelect}
        onGeocode={onGeocode}
      />
    </div>
  );
};

export { OrderAddressSelector };
